import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../utils/date";
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import DateTimeInput from "../components/DateTimeInput";
import Avatar from "../components/Avatar";
import { useAuth } from "../contexts/AuthContext";
import useModules from "../hooks/useModules";
import { api, formatApiError } from "../lib/api";
import { ROLES } from "../lib/rbac";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, Fingerprint, Trash2 } from "lucide-react";
import { TableSkeleton } from "../components/SkeletonHelpers";
import EmptyState from "../components/EmptyState";
import MapBiometricDialog from "../components/MapBiometricDialog";

// Bug 1 helper — bump the shared invalidation timestamp so Attendance.jsx (and any
// other page listening on the same key) refetches when the operator navigates
// back / tabs back. Also used by the Purge button below.
const bumpAttendanceCache = () => {
    try { localStorage.setItem("attendance.invalidated_at", String(Date.now())); } catch { /* ignore */ }
};

export default function AttendanceExceptions() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { byId: modulesById } = useModules();
    const canAct = [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_DEPT_MGR].includes(user?.role);
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState({});
    const [devices, setDevices] = useState({});
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [newTime, setNewTime] = useState("");
    const [mapping, setMapping] = useState(null);  // unmapped-biometric drawer state

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/attendance/events/exceptions");
            setItems(data);
            // Fetch all active employees, paginated (API caps page_size at 200)
            const m = {};
            let page = 1;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const empResp = await api.get("/employees", { params: { page_size: 200, page } });
                const list = empResp.data?.items || [];
                list.forEach((e) => { m[e.id] = e; });
                if (list.length < 200) break;
                page += 1;
                if (page > 20) break; // safety cap (4000 employees)
            }
            setEmployees(m);
            const dev = await api.get("/devices");
            const dm = {};
            dev.data.forEach((d) => { dm[d.id] = d; });
            setDevices(dm);
        } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const canPurge = user?.role === ROLES.KAAFI_ADMIN;
    const [purgeOpen, setPurgeOpen] = useState(false);
    const [purgeBusy, setPurgeBusy] = useState(false);
    const emptyUseridCount = useMemo(
        () => items.filter(
            (ev) => ev.exception_reason === "unmapped_biometric_user_id" &&
                    !(ev.raw_biometric_user_id || "").trim(),
        ).length,
        [items],
    );

    const approve = async (ev) => {
        try { await api.post(`/attendance/events/${ev.id}/approve`); bumpAttendanceCache(); toast.success("Approved"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const reject = async (ev) => {
        try { await api.post(`/attendance/events/${ev.id}/reject`); bumpAttendanceCache(); toast.success("Rejected"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const reclassify = async (ev, targetType) => {
        try {
            await api.post(`/attendance/events/${ev.id}/reclassify`, null, {
                params: { new_type: targetType },
            });
            bumpAttendanceCache();
            toast.success(`Reclassified as ${targetType.replace("_", " ")} — moved to daily row.`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const purgeEmpty = async () => {
        setPurgeBusy(true);
        try {
            const { data } = await api.post("/attendance/events/exceptions/purge-empty-userid");
            bumpAttendanceCache();
            toast.success(`Purged ${data.deleted} unmapped-USERID row${data.deleted === 1 ? "" : "s"}.`);
            setPurgeOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPurgeBusy(false); }
    };
    const submitEdit = async () => {
        if (!editing || !newTime) return;
        try {
            await api.post("/attendance/events", {
                employee_id: editing.employee_id,
                event_type: editing.event_type,
                event_time: new Date(newTime).toISOString(),
                source: "manual",
                verification_method: "manual_entry",
                notes: `Corrected from original event ${editing.id}`,
            });
            await api.post(`/attendance/events/${editing.id}/reject`);
            bumpAttendanceCache();
            toast.success("Corrected event saved");
            setEditing(null);
            setNewTime("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Attendance Exception Queue">
            <section className="space-y-5" data-testid="exceptions-page">
                <div className="flex items-start justify-between gap-4">
                    <p className="text-sm text-[color:var(--asl-muted)]">
                        Events awaiting supervisor review or flagged with a missing pair. Approve to accept, Reject to dismiss, Edit Time to file a corrected manual event,
                        or Map (for unmapped biometric USERIDs) to bind them to an employee.
                    </p>
                    {canPurge && emptyUseridCount > 0 && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0 border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)] hover:bg-[color:var(--asl-crimson-50,#FDF2F5)]"
                            onClick={() => setPurgeOpen(true)}
                            data-testid="exc-purge-empty-btn"
                            title="Delete all rows where the device sent no employeeNoString — these can never be mapped."
                        >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Purge Unmapped USERIDs ({emptyUseridCount})
                        </Button>
                    )}
                </div>
                <div className="asl-card overflow-hidden">
                    {loading ? <TableSkeleton rows={5} cols={7} testid="exceptions-loading" /> :
                     items.length === 0 ? (
                        <EmptyState
                            icon={CheckCircle2}
                            title="No exceptions — all clear ✓"
                            description="When a punch is missing or flagged, it will land here for supervisor review."
                            testid="exceptions-empty"
                        />
                     ) : (
                        <table className="asl-table asl-table--zebra" data-testid="exceptions-table">
                            <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}>
                                <tr>
                                    <th className="text-left px-4 py-3">Employee / Source</th>
                                    <th className="text-left px-4 py-3">Module</th>
                                    <th className="text-left px-4 py-3">Event Time</th>
                                    <th className="text-left px-4 py-3">Type</th>
                                    <th className="text-left px-4 py-3">Source</th>
                                    <th className="text-left px-4 py-3">Status / Reason</th>
                                    <th className="text-right px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((ev) => {
                                    const emp = employees[ev.employee_id];
                                    const isUnmapped = ev.exception_reason === "unmapped_biometric_user_id";
                                    const isUnknownStatus = ev.exception_reason === "unknown_attendance_status_0";
                                    const dev = devices[ev.device_id];
                                    return (
                                        <tr key={ev.id} className="border-t" style={{ borderColor: "var(--asl-border)" }}
                                            data-testid={`exception-row-${ev.id}`}>
                                            <td className="px-4 py-3">
                                                {isUnmapped ? (
                                                    <div className="flex items-center gap-2">
                                                        <Fingerprint className="h-4 w-4 asl-crimson-text" />
                                                        <div>
                                                            <div className="font-medium asl-navy-text">Unmapped USERID</div>
                                                            <div className="mono text-[11px] asl-crimson-text">
                                                                #{ev.raw_biometric_user_id} on {dev?.serial_number || "—"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <Avatar src={emp?.photo_url} name={emp?.full_name} size={32} />
                                                        <div>
                                                            <div className="font-medium asl-navy-text">{emp?.full_name || "—"}</div>
                                                <div className="mono text-[11px] asl-crimson-text">{emp?.module_employee_id || ""}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-xs">{modulesById[ev.module_id]?.code || "—"}</td>
                                            <td className="px-4 py-3 mono text-xs">{formatDateTime(ev.event_time)}</td>
                                            <td className="px-4 py-3 text-xs">{ev.event_type}</td>
                                            <td className="px-4 py-3 text-xs">{ev.source}</td>
                                            <td className="px-4 py-3 text-xs">
                                                <div className="font-medium">{ev.status}</div>
                                                {ev.exception_reason && (
                                                    <div className="asl-crimson-text mono text-[11px]"
                                                         title={isUnknownStatus
                                                             ? "Device sent this scan without a Check In / Check Out selection. Reclassify below or ask the operator to tap the button before scanning next time."
                                                             : ev.exception_reason}>
                                                        {isUnknownStatus ? "Unknown attendance status" : ev.exception_reason}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right space-x-1">
                                                {canAct && (
                                                    isUnmapped ? (
                                                        <>
                                                            <Button size="sm" className="asl-navy-bg text-white"
                                                                    onClick={() => setMapping({ ev, dev })}
                                                                    data-testid={`exc-map-${ev.id}`}>
                                                                Map
                                                            </Button>
                                                            <Button size="sm" variant="outline" onClick={() => reject(ev)} data-testid={`exc-reject-${ev.id}`}>Reject</Button>
                                                        </>
                                                    ) : isUnknownStatus ? (
                                                        <>
                                                            <Button size="sm" className="asl-navy-bg text-white"
                                                                    onClick={() => reclassify(ev, "clock_in")}
                                                                    data-testid={`exc-reclassify-in-${ev.id}`}>
                                                                Reclassify as Check In
                                                            </Button>
                                                            <Button size="sm" className="asl-crimson-bg text-white"
                                                                    onClick={() => reclassify(ev, "clock_out")}
                                                                    data-testid={`exc-reclassify-out-${ev.id}`}>
                                                                Reclassify as Check Out
                                                            </Button>
                                                            <Button size="sm" variant="outline" onClick={() => reject(ev)} data-testid={`exc-reject-${ev.id}`}>Reject</Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Button size="sm" variant="outline" onClick={() => approve(ev)} data-testid={`exc-approve-${ev.id}`}>Approve</Button>
                                                            <Button size="sm" variant="outline" onClick={() => reject(ev)} data-testid={`exc-reject-${ev.id}`}>Reject</Button>
                                                            <Button size="sm" variant="outline" onClick={() => { setEditing(ev); setNewTime(ev.event_time.slice(0, 16)); }} data-testid={`exc-edit-${ev.id}`}>Edit Time</Button>
                                                        </>
                                                    )
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent data-testid="exception-edit-dialog">
                    <DialogHeader><DialogTitle>Correct Event Time</DialogTitle></DialogHeader>
                    <p className="text-xs text-[color:var(--asl-muted)]">A new manual event will be created at the corrected time; the original is rejected.</p>
                    <DateTimeInput value={newTime} onChange={setNewTime} data-testid="exc-edit-time" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button onClick={submitEdit} className="asl-navy-bg text-white" data-testid="exc-edit-submit">Save corrected time</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={purgeOpen} onOpenChange={(o) => !o && setPurgeOpen(false)}>
                <DialogContent data-testid="exc-purge-dialog">
                    <DialogHeader>
                        <DialogTitle>Purge Unmapped USERID rows?</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm space-y-3">
                        <p>
                            This will permanently delete <span className="font-semibold asl-crimson-text">{emptyUseridCount}</span> exception row{emptyUseridCount === 1 ? "" : "s"} where
                            the device sent an empty <span className="mono text-xs">employeeNoString</span>. These are face-detection events for strangers /
                            failed-verify scans — they cannot be mapped to any employee.
                        </p>
                        <p className="text-xs text-[color:var(--asl-muted)]">
                            Legitimate unmapped-USERID rows (where a real numeric ID was sent but doesn't match ASL) will <span className="font-semibold">not</span> be touched.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPurgeOpen(false)} disabled={purgeBusy}>Cancel</Button>
                        <Button className="asl-crimson-bg text-white" onClick={purgeEmpty} disabled={purgeBusy}
                                data-testid="exc-purge-confirm">
                            {purgeBusy ? "Purging…" : `Delete ${emptyUseridCount} row${emptyUseridCount === 1 ? "" : "s"}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <MapBiometricDialog
                state={mapping}
                onClose={() => setMapping(null)}
                onMapped={() => { setMapping(null); load(); }}
                items={items}
                employees={Object.values(employees)}
            />
        </AppShell>
    );
}
